import { ChangeEvent, useState, useEffect } from 'react';
import styled from 'styled-components';

import {
  cloudBurst,
  paradigmLightBlue,
} from '@paradigm/design-system/src/colors';

import SearchIcon from '@paradigm/design-system/src/assets/SearchIcon';
import CloseIcon from '@paradigm/design-system/src/assets/CloseIcon';
import UnstyledButton from '#/components/buttons/UnstyledButton';
import { useCounterpartiesService } from '@paradigm/features/src/unified-rfqs/contexts/counterparties';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { useDebouncedTrackAction } from '@paradigm/features/src/analytics';

export default function SearchInput() {
  const [search, _setSearch] = useState('');
  const cpsService = useCounterpartiesService();
  const trackAction = useDebouncedTrackAction();

  const setSearch = (value: string) => {
    _setSearch(value);
    cpsService.search.setDebounced(value);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    trackAction('search counterparties', { searchTerm: value });
    setSearch(value);
  };

  useEffect(() => {
    cpsService.search.setDebounced('');
  }, [cpsService.search]);

  const handleClean = () => {
    setSearch('');
  };

  const shouldShowCleanBtn = search.length > 0;

  return (
    <Container>
      <SearchIcon />
      <Input placeholder="Search" onChange={handleChange} value={search} />
      {shouldShowCleanBtn && <CleanButton onClick={handleClean} />}
    </Container>
  );
}

const Input = styled.input`
  height: 100%;
  background: none;
  border: 0;
  outline: 0;
  padding: 0;
  color: white;

  ::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 1rem;
  }
`;

const Container = styled.div`
  display: grid;
  grid-template-columns: 14px 1fr 15px;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 11px;
  border-radius: 17px;
  background: #0f2038;
  margin: 0 0 0.75rem;
  transition: box-shadow 0.1s ease;

  :has(${Input}:focus-visible) {
    box-shadow: 0 0 0 1px ${paradigmLightBlue};
  }

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    background: ${cloudBurst};
    margin: 0.5rem 0 1rem 0;
  }
`;

const CleanButton = styled(UnstyledButton).attrs({
  'aria-label': 'clean search',
  type: 'button',
  children: <CloseIcon />,
})`
  display: grid;
  place-items: center;
  padding: 3px;
  border-radius: 3px;
  color: white;
  transition: color 0.1s ease, box-shadow 0.1s ease;

  :hover {
    color: ${paradigmLightBlue};
  }

  :focus-visible {
    box-shadow: 0 0 0 1px ${paradigmLightBlue};
  }
`;
